import { ReactNode, useCallback, useId, useMemo, useState } from "react";
import { CalendarDays, Check, ChevronDown, ChevronRight, Clock, LucideIcon, Moon, StickyNote, Sun, X } from "lucide-react";
import { Button } from "../../shadcn/button";
import { ToggleGroup, ToggleGroupItem } from "../../shadcn/toggle-group";
import { cn } from "../../../lib/cn";
import { DayType } from "./api";
import { ActivityTiles, ACTIVITIES } from "./ActivityTiles";
import { MinutesChips } from "./MinutesChips";
import { NightDraft, addDays, addMinutes, ampm, dayLabel, previewMetrics } from "./nightClock";
import { TimePicker } from "./TimePicker";

const BLOCKS = [0, 1, 2, 3, 4];

export type NightAccordion = {
  /** The open block, or -1 when all are closed. */
  active: number;
  done: Set<number>;
  open(i: number): void;
  next(i: number): void;
  reset(saved: boolean): void;
};

const initialDone = (saved: boolean) => new Set(saved ? BLOCKS : []);

/** One block open at a time; a saved night opens with every block closed and done. */
export function useNightAccordion(saved: boolean): NightAccordion {
  const [active, setActive] = useState(saved ? -1 : 0);
  const [done, setDone] = useState<Set<number>>(() => initialDone(saved));

  const open = useCallback((i: number) => setActive(i), []);
  const next = useCallback(
    (i: number) => {
      const nextDone = new Set(done).add(i);
      const after = [1, 2, 3, 4].map((d) => (i + d) % 5).find((j) => !nextDone.has(j));
      setDone(nextDone);
      setActive(after === undefined ? -1 : after);
    },
    [done]
  );
  const reset = useCallback((s: boolean) => {
    setActive(s ? -1 : 0);
    setDone(initialDone(s));
  }, []);

  return useMemo(() => ({ active, done, open, next, reset }), [active, done, open, next, reset]);
}

/** The block a server-refused field lives in; -1 for page-level fields (`day`, `timeZone`). */
export function blockOfField(field: string): number {
  const has = (prefix: string) => field.startsWith(prefix);
  if (has("inBed") || has("sleepLatency")) return 0;
  if (has("awakening")) return 1;
  if (has("wake") || has("outOfBed")) return 2;
  if (has("dayType") || has("activities")) return 3;
  if (has("notes")) return 4;
  return -1;
}

const LATENCY = [
  { value: 5, label: "Under 5 min" },
  { value: 15, label: "15 min" },
  { value: 30, label: "30 min" },
  { value: 45, label: "45 min" },
  { value: 60, label: "1 hour" },
];
const AWAKE_TOTAL = [
  { value: 5, label: "A few minutes" },
  { value: 15, label: "15 min" },
  { value: 30, label: "30 min" },
  { value: 60, label: "1 hour" },
];
const OUT_OF_BED = [
  { value: 0, label: "Right away" },
  { value: 15, label: "15 min later" },
  { value: 30, label: "30 min later" },
  { value: 60, label: "1 hour later" },
];
const AWAKENINGS = ["No", "Once", "Twice", "3 times", "4 or more"];
const AWAKENINGS_SUMMARY = ["", "Once", "Twice", "3 times", "4 or more times"];
const DAY_TYPES: { value: DayType; label: string }[] = [
  { value: "work", label: "Work" },
  { value: "day-off", label: "Day off" },
  { value: "sick-day", label: "Sick day" },
  { value: "vacation", label: "Vacation" },
  { value: "other", label: "Other" },
];
const MAX_AWAKENING_TIMES = 4;

const latencyText = (n: number) => (n < 60 ? `${n} min` : `${Math.round(n / 6) / 10} h`);

/** The one-line summary of a collapsed block (mockup:638-644). */
export function blockSummary(i: number, draft: NightDraft): string {
  switch (i) {
    case 0:
      return `In bed ${ampm(draft.inBed)}, asleep in ${latencyText(draft.sleepLatencyMinutes)}`;
    case 1:
      if (draft.awakeningCount === null) return "Not answered yet";
      if (draft.awakeningCount === 0) return "Slept through";
      return `${AWAKENINGS_SUMMARY[Math.min(draft.awakeningCount, 4)]}, ${draft.awakeningMinutes} min awake`;
    case 2:
      return (
        `Up at ${ampm(draft.wake)}` +
        (draft.outOfBedAfterMinutes ? `, out of bed ${draft.outOfBedAfterMinutes} min later` : ", out of bed right away")
      );
    case 3: {
      const day = DAY_TYPES.find((d) => d.value === draft.dayType)?.label ?? draft.dayType;
      const acts = ACTIVITIES.filter((a) => draft.activities.some((v) => v.type === a.type)).map((a) => a.label);
      return `${day}, ${acts.length ? acts.join(", ") : "nothing else"}`;
    }
    case 4:
      return draft.notes || "Nothing added";
    default:
      return "";
  }
}

export type NightFormProps = {
  draft: NightDraft;
  onChange(next: NightDraft): void;
  accordion: NightAccordion;
  day: string;
  isLastNight: boolean;
  fieldError: { field: string; reason: string } | null;
};

const ErrorText = ({ children }: { children: ReactNode }) => (
  <p className="mt-1 text-[13px] text-red-600">{children}</p>
);

const Label = ({ children }: { children: ReactNode }) => <div className="font-medium">{children}</div>;

/** Grid-rows reveal: 0fr -> 1fr with a fade; reduced motion fades only. Closed content leaves the tab order. */
function Reveal({ open, id, children }: { open: boolean; id?: string; children: ReactNode }) {
  return (
    <div
      id={id}
      className={cn(
        "grid transition-[grid-template-rows,opacity] duration-200 ease-soft motion-reduce:transition-opacity",
        open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
      )}
    >
      <div className="overflow-hidden" aria-hidden={!open} {...(open ? {} : { inert: "" })}>
        {children}
      </div>
    </div>
  );
}

const STEP_ICONS: LucideIcon[] = [Moon, Clock, Sun, CalendarDays, StickyNote];

/** The five-block night form (mockup-v4:264-337). Stateless except the "More" fields. */
export function NightForm({ draft, onChange, accordion, day, isLastNight, fieldError }: NightFormProps) {
  const baseId = useId();
  const set = (patch: Partial<NightDraft>) => onChange({ ...draft, ...patch });

  const preview = previewMetrics(draft);
  const wakeInvalid = !preview.ok && preview.field === "wake";
  const awakeTooLong = !preview.ok && preview.field === "awakeningMinutes";
  const serverBlock = fieldError ? blockOfField(fieldError.field) : -1;

  const titles = ["Going to bed", "During the night", "Waking up", "Your day", "Notes"];
  const subs = isLastNight
    ? ["Yesterday evening", "Times you were awake for a while", "This morning", "Yesterday", "Optional"]
    : [
        `Evening of ${dayLabel(day)}`,
        "Times you were awake for a while",
        `Morning of ${dayLabel(addDays(day, 1))}`,
        dayLabel(day),
        "Optional",
      ];

  const count = draft.awakeningCount;
  const pickAwakenings = (v: number) => {
    if (v === 0) return set({ awakeningCount: 0, awakeningMinutes: 0, awakeningTimes: [] });
    set({
      awakeningCount: v,
      awakeningMinutes: draft.awakeningMinutes === 0 ? 15 : draft.awakeningMinutes,
      awakeningTimes: draft.awakeningTimes.slice(0, v),
    });
  };

  const bodies: ReactNode[] = [
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Label>Got into bed</Label>
        <TimePicker label="Got into bed" value={draft.inBed} onChange={(inBed) => set({ inBed })} />
      </div>
      <div className="mt-4">
        <Label>Took to fall asleep</Label>
        <div className="mt-2">
          <MinutesChips
            label="Took to fall asleep"
            options={LATENCY}
            value={draft.sleepLatencyMinutes}
            onChange={(sleepLatencyMinutes) => set({ sleepLatencyMinutes })}
          />
        </div>
        <div className="mt-2 text-[13px] text-neutral-500">
          Asleep around <b className="font-semibold text-neutral-950">{ampm(addMinutes(draft.inBed, draft.sleepLatencyMinutes))}</b>
        </div>
      </div>
    </>,
    <>
      <div>
        <Label>Woke up</Label>
        <ToggleGroup
          type="single"
          variant="chip"
          aria-label="Woke up"
          className="mt-2 gap-1"
          value={count === null ? "" : String(Math.min(count, 4))}
          onValueChange={(v) => {
            if (!v) return; // Radix sends "" when the selected chip is clicked again.
            pickAwakenings(Number(v));
          }}
        >
          {AWAKENINGS.map((label, v) => (
            <ToggleGroupItem key={v} value={String(v)}>
              {label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <Reveal open={!!count && count > 0}>
        <div className="pt-3">
          <Label>Awake in total</Label>
          <div className="mt-2">
            <MinutesChips
              label="Awake in total"
              options={AWAKE_TOTAL}
              value={draft.awakeningMinutes}
              min={1}
              onChange={(awakeningMinutes) => set({ awakeningMinutes })}
            />
          </div>
          {awakeTooLong && <ErrorText>This is longer than the time you were asleep.</ErrorText>}
          <div className="mt-2">
            {draft.awakeningTimes.map((t, i) => (
              <div
                key={i}
                className={cn("flex flex-wrap items-center gap-2 py-2", i > 0 && "border-t border-dashed border-neutral-200")}
              >
                <span className="w-[120px] text-[13px] font-medium">Wake-up {i + 1}</span>
                <TimePicker
                  size="sm"
                  label={`Wake-up ${i + 1} time`}
                  value={t}
                  onChange={(v) => set({ awakeningTimes: draft.awakeningTimes.map((x, j) => (j === i ? v : x)) })}
                />
                <button
                  type="button"
                  aria-label={`Remove wake-up ${i + 1}`}
                  className="grid h-7 w-7 place-items-center rounded-md text-neutral-400 transition-colors duration-150 ease-soft hover:bg-neutral-50 hover:text-neutral-950"
                  onClick={() => set({ awakeningTimes: draft.awakeningTimes.filter((_, j) => j !== i) })}
                >
                  <X size={14} aria-hidden />
                </button>
              </div>
            ))}
          </div>
          {count !== null && draft.awakeningTimes.length < Math.min(count, MAX_AWAKENING_TIMES) && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="px-0 text-neutral-500"
              onClick={() => set({ awakeningTimes: [...draft.awakeningTimes, "03:00"] })}
            >
              Add when it happened (optional)
            </Button>
          )}
        </div>
      </Reveal>
    </>,
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Label>Woke up for the day</Label>
          {wakeInvalid && <ErrorText>This is before you fell asleep.</ErrorText>}
        </div>
        <TimePicker label="Woke up for the day" value={draft.wake} invalid={wakeInvalid} onChange={(wake) => set({ wake })} />
      </div>
      <div className="mt-4">
        <Label>Got out of bed</Label>
        <div className="mt-2">
          <MinutesChips
            label="Got out of bed"
            options={OUT_OF_BED}
            value={draft.outOfBedAfterMinutes}
            onChange={(outOfBedAfterMinutes) => set({ outOfBedAfterMinutes })}
          />
        </div>
      </div>
    </>,
    <>
      <div>
        <Label>Kind of day</Label>
        <ToggleGroup
          type="single"
          variant="chip"
          aria-label="Kind of day"
          className="mt-2 gap-1"
          value={draft.dayType}
          onValueChange={(v) => {
            if (v) set({ dayType: v as DayType });
          }}
        >
          {DAY_TYPES.map((d) => (
            <ToggleGroupItem key={d.value} value={d.value}>
              {d.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <ActivityTiles value={draft.activities} onChange={(activities) => set({ activities })} />
    </>,
    <textarea
      aria-label="Notes"
      placeholder="Anything else about your sleep"
      maxLength={2000}
      value={draft.notes}
      onChange={(e) => set({ notes: e.target.value })}
      className="min-h-[80px] w-full resize-y rounded-[10px] border border-neutral-200 px-3 py-2 outline-none transition duration-150 ease-soft focus:border-brand focus:shadow-ring"
    />,
  ];

  return (
    <div>
      {BLOCKS.map((i) => {
        const isOpen = accordion.active === i;
        const isDone = accordion.done.has(i);
        const showCheck = isDone && !isOpen;
        const Icon = STEP_ICONS[i];
        const bodyId = `${baseId}-block-${i}`;
        const othersDone = BLOCKS.every((j) => j === i || accordion.done.has(j));
        return (
          <section key={i} className="border-neutral-200 [&:not(:first-child)]:border-t">
            <button
              type="button"
              aria-expanded={isOpen}
              aria-controls={bodyId}
              onClick={() => {
                if (!isOpen) accordion.open(i);
              }}
              className={cn(
                "flex w-full items-center gap-4 px-5 py-4 text-left transition-colors duration-150 ease-soft",
                isOpen ? "cursor-default" : "hover:bg-neutral-50"
              )}
            >
              <span
                className={cn(
                  "grid h-8 w-8 shrink-0 place-items-center rounded-full border transition-colors duration-150 ease-soft",
                  showCheck
                    ? "border-green-200 bg-green-50 text-green-600"
                    : isOpen
                      ? "border-neutral-950 text-neutral-950"
                      : "border-neutral-200 text-neutral-500"
                )}
              >
                {showCheck ? <Check size={14} aria-hidden /> : <Icon size={16} aria-hidden />}
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="font-semibold">{titles[i]}</span>
                <span className="text-[13px] text-neutral-500">{subs[i]}</span>
              </span>
              <span
                className={cn(
                  "ml-auto max-w-[55%] truncate text-right text-[13px] text-neutral-500 transition-opacity duration-150 ease-soft max-[859px]:hidden",
                  isOpen && "opacity-0"
                )}
              >
                {blockSummary(i, draft)}
              </span>
              {showCheck && <span className="sr-only">Completed</span>}
              <ChevronDown
                size={16}
                aria-hidden
                className={cn(
                  "shrink-0 text-neutral-400 transition-transform duration-200 ease-soft",
                  isOpen && "rotate-180"
                )}
              />
            </button>
            <Reveal open={isOpen} id={bodyId}>
              <div className="px-5 pb-5 min-[860px]:pl-[68px]">
                {bodies[i]}
                {serverBlock === i && fieldError && <ErrorText>{fieldError.reason}</ErrorText>}
                <div className="mt-5 flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={i === 1 && count === null}
                    className="disabled:opacity-40"
                    onClick={() => accordion.next(i)}
                  >
                    {othersDone ? (
                      "Done"
                    ) : (
                      <>
                        Looks right, next <ChevronRight size={14} aria-hidden />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Reveal>
          </section>
        );
      })}
    </div>
  );
}
